import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2, Save, AlertTriangle, TrendingDown, Activity, Shield, FileText, HeartHandshake, TrendingUp, Brain } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import AppShell from "@/components/AppShell";
import SharedFormattedMarkdown from "@/components/FormattedMarkdown";
import api, { formatApiError } from "@/lib/api";

const BAND_META = {
    critical: { color: "text-[#9E2A2B]", bg: "bg-[#F9ECEC]", label: "Crítico", Icon: AlertTriangle },
    high: { color: "text-[#C85A47]", bg: "bg-[#FBEEE6]", label: "Alto risco", Icon: AlertTriangle },
    moderate: { color: "text-[#9C6E28]", bg: "bg-[#FBF4E5]", label: "Moderado", Icon: TrendingDown },
    low: { color: "text-[#556B2F]", bg: "bg-[#EDF3E8]", label: "Baixo risco", Icon: Shield },
};

const SEV_CHIP = {
    high: "bg-[#9E2A2B] text-white",
    medium: "bg-[#D4A373] text-white",
    low: "bg-[#556B2F] text-white",
};

const DOMAIN_LABEL = {
    functional: "Funcional", falls: "Quedas", mobility: "Mobilidade",
    cognitive: "Cognição", mood: "Humor", frailty: "Fragilidade",
    nutrition: "Nutrição", sarcopenia: "Sarcopenia",
    medications: "Medicamentos", cardiovascular: "Cardiovascular",
    transition: "Transição de cuidados",
};

export default function PatientRiskDetail() {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [planText, setPlanText] = useState("");
    const [generating, setGenerating] = useState(false);

    // Level 3 additions
    const [trajectory, setTrajectory] = useState(null);
    const [predictionText, setPredictionText] = useState("");
    const [predicting, setPredicting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [r, t] = await Promise.allSettled([
                    api.get(`/monitoring/patient/${patientId}`),
                    api.get(`/monitoring/risk-trajectory/${patientId}`),
                ]);
                if (r.status === "fulfilled") setState(r.value.data);
                if (t.status === "fulfilled") setTrajectory(t.value.data);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [patientId]);

    const predict = async () => {
        setPredicting(true); setPredictionText("");
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monitoring/predict-rehospitalization`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ patient_id: patientId, horizon_months: 6 }),
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${resp.status}`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let acc = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                acc += decoder.decode(value, { stream: true });
                setPredictionText(acc);
            }
            toast.success("Predição gerada");
        } catch (e) { toast.error(e.message); }
        finally { setPredicting(false); }
    };

    const generate = async () => {
        setGenerating(true); setPlanText("");
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monitoring/intervention-plan`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ patient_id: patientId }),
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${resp.status}`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let acc = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                acc += decoder.decode(value, { stream: true });
                setPlanText(acc);
            }
            toast.success("Plano de intervenção gerado");
        } catch (e) { toast.error(e.message); }
        finally { setGenerating(false); }
    };

    if (loading) return <AppShell><div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-brand" size={32} /></div></AppShell>;
    if (!state) return <AppShell><p>Sem dados</p></AppShell>;

    const { patient, consultation, report } = state;
    const meta = BAND_META[report.risk_band] || BAND_META.low;
    const Icon = meta.Icon;

    return (
        <AppShell>
            <button onClick={() => navigate(-1)} className="btn-ghost mb-4"><ArrowLeft size={16} /> Voltar</button>

            <div className="grid lg:grid-cols-3 gap-6 mb-6">
                <div className={`card-earthy lg:col-span-2 ${meta.bg} border-l-4 border-current ${meta.color}`}>
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-current mb-2 flex items-center gap-2">
                                <Icon size={14} /> {meta.label}
                            </div>
                            <h1 className="font-display text-3xl font-medium text-brand">{patient.full_name}</h1>
                            <div className="text-sm text-[var(--text-secondary)] mt-1">
                                Última consulta: {consultation.scheduled_at || consultation.created_at?.slice(0, 10)} · {patient.insurance || "—"}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className={`font-display text-5xl font-semibold ${meta.color}`}>{report.risk_score}</div>
                            <div className="text-xs uppercase tracking-wider text-muted-clinical">score</div>
                        </div>
                    </div>
                </div>

                <div className="card-earthy">
                    <h3 className="font-display text-sm font-medium text-brand-secondary uppercase tracking-wider mb-3">Medidas-chave</h3>
                    <div className="text-sm space-y-1.5">
                        <Row label="Katz" val={report.measures.katz} suffix="/6" />
                        <Row label="Lawton" val={report.measures.lawton} suffix="/27" />
                        <Row label="MEEM" val={report.measures.meem} suffix="/30" />
                        <Row label="GDS-15" val={report.measures.gds15} />
                        <Row label="Fried" val={report.measures.fried} suffix="/5" />
                        <Row label="CFS" val={report.measures.cfs} suffix="/9" />
                        <Row label="Quedas 12m" val={report.measures.falls_12m} />
                        <Row label="Medicamentos" val={report.measures.medication_count} />
                        <Row label="IMC" val={report.measures.imc?.toFixed(1)} />
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
                <div>
                    <h2 className="font-display text-xl font-medium text-brand mb-3">Achados clínicos</h2>
                    {report.findings.length === 0 ? (
                        <div className="card-earthy text-sm text-muted-clinical">Nenhum achado de risco identificado.</div>
                    ) : (
                        <div className="space-y-3">
                            {report.findings.map((f, i) => (
                                <div key={i} data-testid={`finding-${i}`} className="card-earthy">
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-wider text-muted-clinical mb-1">{DOMAIN_LABEL[f.domain] || f.domain}</div>
                                            <div className="font-medium text-brand">{f.label}</div>
                                            <div className="text-sm text-[var(--text-secondary)] mt-0.5">{f.detail}</div>
                                        </div>
                                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${SEV_CHIP[f.severity]}`}>
                                            {f.severity === "high" ? "Alto" : f.severity === "medium" ? "Médio" : "Baixo"}
                                        </span>
                                    </div>
                                    <ul className="text-sm space-y-1 mt-2">
                                        {f.recommendations.map((rec, j) => (
                                            <li key={j} className="flex items-start gap-2"><span className="text-[var(--brand-accent)] mt-1">•</span><span>{rec}</span></li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-xl font-medium text-brand">Plano de intervenção assistido por IA</h2>
                        <button data-testid="generate-intervention-btn" onClick={generate} disabled={generating || report.findings.length === 0} className="btn-primary text-sm">
                            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Gerar
                        </button>
                    </div>
                    <div className="card-earthy min-h-[400px]">
                        {planText ? (
                            <FormattedMarkdown text={planText} />
                        ) : (
                            <div className="text-sm text-muted-clinical bg-alt p-4 rounded-lg">
                                Clique em <strong>Gerar</strong> para elaborar um plano de intervenção personalizado (farmacológico + não farmacológico) com metas SMART e sinais de piora.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Trajectory (Level 3) + LLM prediction */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
                <div className="card-earthy" data-testid="risk-trajectory">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-lg font-medium text-brand flex items-center gap-2">
                            <TrendingUp size={16} /> Trajetória de risco (12 meses)
                        </h2>
                        {trajectory && (
                            <span className={`text-xs font-semibold ${trajectory.delta > 0 ? "text-[#9E2A2B]" : trajectory.delta < 0 ? "text-[#556B2F]" : "text-muted-clinical"}`}>
                                Δ {trajectory.delta > 0 ? "+" : ""}{trajectory.delta}
                            </span>
                        )}
                    </div>
                    {!trajectory || trajectory.series.length === 0 ? (
                        <p className="text-sm text-muted-clinical bg-alt p-4 rounded-lg">
                            Ainda sem histórico de perfis de risco salvos. Abra o Snapshot 360° algumas vezes para começar a construir a série temporal.
                        </p>
                    ) : trajectory.series.length === 1 ? (
                        <p className="text-sm text-muted-clinical bg-alt p-4 rounded-lg">
                            Apenas 1 ponto disponível ({trajectory.series[0].date} — score {trajectory.series[0].score}). Retorne em consultas futuras para visualizar a evolução.
                        </p>
                    ) : (
                        <div className="h-56" data-testid="trajectory-chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={trajectory.series} margin={{ top: 8, right: 12, left: -8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,63,51,0.08)" />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 10, fill: "#6B7280" }}
                                        tickFormatter={(d) => new Date(d).toLocaleDateString("pt-BR", { month: "short", day: "2-digit" })}
                                    />
                                    <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} allowDecimals={false} />
                                    <Tooltip
                                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                                        labelFormatter={(d) => new Date(d).toLocaleDateString("pt-BR")}
                                    />
                                    <ReferenceLine y={7} stroke="#C85A47" strokeDasharray="4 4" label={{ value: "Alto", fontSize: 9, fill: "#C85A47" }} />
                                    <ReferenceLine y={12} stroke="#9E2A2B" strokeDasharray="4 4" label={{ value: "Crítico", fontSize: 9, fill: "#9E2A2B" }} />
                                    <Line type="monotone" dataKey="score" stroke="#1E3F33" strokeWidth={2.5} dot={{ r: 3, fill: "#1E3F33" }} activeDot={{ r: 5 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="card-earthy" data-testid="llm-prediction">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-lg font-medium text-brand flex items-center gap-2">
                            <Brain size={16} /> Predição LLM (6 meses)
                        </h2>
                        <button
                            data-testid="predict-btn"
                            onClick={predict}
                            disabled={predicting}
                            className="btn-primary text-sm"
                        >
                            {predicting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Estimar
                        </button>
                    </div>
                    <div className="min-h-[220px] max-h-[420px] overflow-y-auto">
                        {predictionText ? (
                            <FormattedMarkdown text={predictionText} />
                        ) : (
                            <div className="text-sm text-muted-clinical bg-alt p-4 rounded-lg">
                                Clique em <strong>Estimar</strong> para gerar uma predição orientativa do risco de rehospitalização em 6 meses, com fatores de risco, protetores e ações sugeridas. Baseado em Claude Sonnet 4.5.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="card-earthy bg-alt border-l-4 border-[var(--brand-secondary)]">
                <div className="flex items-center gap-3">
                    <HeartHandshake size={20} className="text-[var(--brand-secondary)]" />
                    <div className="flex-1">
                        <div className="font-medium text-brand">Avaliar sobrecarga do cuidador</div>
                        <div className="text-sm text-muted-clinical">O acompanhamento do cuidador é parte do cuidado do idoso.</div>
                    </div>
                    <Link to={`/caregiver-monitoring?patient=${patientId}`} className="btn-secondary text-sm">Escala de Zarit</Link>
                </div>
            </div>
        </AppShell>
    );
}

function Row({ label, val, suffix }) {
    return (
        <div className="flex justify-between gap-2">
            <span className="text-muted-clinical">{label}</span>
            <span className={`font-medium ${val == null ? "text-muted-clinical" : "text-brand"}`}>{val == null ? "—" : `${val}${suffix || ""}`}</span>
        </div>
    );
}

function FormattedMarkdown({ text }) {
    return <SharedFormattedMarkdown text={text} />;
}
